
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, X, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addUserToClientVisibility, removeUserFromClientVisibility } from "@/lib/supabase-functions";

interface ClientVisibilityDialogProps {
  clientId: string;
  clientName: string;
  visibleTo: string[];
  onUpdate: () => void;
}

interface User {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
}

export function ClientVisibilityDialog({ clientId, clientName, visibleTo, onUpdate }: ClientVisibilityDialogProps) {
  const [open, setOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [visibleUsers, setVisibleUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const loadUsers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar empresa do usuário
      let targetCompanyId: string | null = null;

      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
      } else {
        // Buscar própria empresa
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        
        targetCompanyId = userData?.company_id || null;
      }

      if (!targetCompanyId) return;

      // Buscar proprietários da empresa
      const { data: owners } = await supabase
        .from('users')
        .select('id, name, email, avatar_url')
        .eq('company_id', targetCompanyId);

      // Buscar colaboradores ativos da empresa
      const { data: collaborators } = await supabase
        .from('collaborators')
        .select(`
          collaborator_id,
          users:collaborator_id (
            id, name, email, avatar_url
          )
        `)
        .eq('company_id', targetCompanyId)
        .eq('status', 'active');

      const allUsers: User[] = [];
      
      // Adicionar proprietários
      if (owners) {
        allUsers.push(...owners);
      }

      // Adicionar colaboradores
      if (collaborators) {
        collaborators.forEach(collab => {
          const collaboratorUser = collab.users as any;
          if (collaboratorUser && !allUsers.find(u => u.id === collaboratorUser.id)) {
            allUsers.push(collaboratorUser);
          }
        });
      }

      setAvailableUsers(allUsers);

      // Buscar usuários visíveis atuais
      const visible = allUsers.filter(u => visibleTo.includes(u.id));
      setVisibleUsers(visible);

    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erro ao carregar usuários');
    }
  };

  const addUserToVisibility = async () => {
    if (!selectedUserId) return;

    setIsLoading(true);
    try {
      await addUserToClientVisibility(clientId, selectedUserId);

      toast.success('Usuário adicionado com sucesso');
      setSelectedUserId("");
      onUpdate();
      loadUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Erro ao adicionar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  const removeUserFromVisibility = async (userId: string) => {
    setIsLoading(true);
    try {
      await removeUserFromClientVisibility(clientId, userId);

      toast.success('Usuário removido com sucesso');
      onUpdate();
      loadUsers();
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error('Erro ao remover usuário');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const availableToAdd = availableUsers.filter(u => !visibleTo.includes(u.id));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Users className="h-4 w-4 mr-2" />
          Compartilhar ({visibleTo.length})
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Compartilhar Cliente</DialogTitle>
          <DialogDescription>
            Gerencie quem pode ver o cliente "{clientName}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Adicionar usuário */}
          <div className="flex gap-2">
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Selecionar usuário..." />
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={addUserToVisibility} 
              disabled={!selectedUserId || isLoading}
              size="sm"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Lista de usuários com acesso */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Usuários com acesso:</h4>
            {visibleUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum usuário com acesso</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {visibleUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-2 rounded border">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        {user.avatar_url ? (
                          <AvatarImage src={user.avatar_url} alt={user.name} />
                        ) : (
                          <AvatarFallback className="text-xs">
                            {(user.name || user.email).charAt(0).toUpperCase()}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{user.name || 'Sem nome'}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUserFromVisibility(user.id)}
                      disabled={isLoading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
